import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Layout from "../layout/Layout";
import Button from "../components/Button";
import {
  addVaAccessAPI,
  getSubscriptionSettingsAPI,
  getVaAccessListAPI,
  getAmazonIntegrationUrlAPI,
  getDiscordIntegrationUrlAPI,
  removeVaAccessAPI,
} from "../lib/api/api";
import { toast } from "react-toastify";

const InfoItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs font-semibold uppercase tracking-wide text-textPrimary">
      {label}
    </span>
    <span className="text-sm md:text-base text-textTitle dark:text-white break-words">
      {value || "—"}
    </span>
  </div>
);

const IntegrationStatusBadge = ({ isConnected }) => {
  const baseClasses =
    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold";
  const connectedClasses =
    "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-emerald-200";
  const disconnectedClasses =
    "border-red-300 bg-red-50 text-red-600 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200";

  return (
    <span
      className={`${baseClasses} ${
        isConnected ? connectedClasses : disconnectedClasses
      }`}
    >
      <i
        className={`bx ${
          isConnected ? "bx-link-alt" : "bx-unlink"
        } text-sm leading-none`}
      ></i>
      {isConnected ? "Verbunden" : "Nicht verbunden"}
    </span>
  );
};

const IntegrationCard = ({
  title,
  isConnected,
  identifierLabel,
  identifierValue,
  action,
}) => {
  const statusContent = isConnected
    ? <IntegrationStatusBadge isConnected />
    : action ?? <IntegrationStatusBadge isConnected={false} />;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-borderBottom bg-background p-4 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-textTitle dark:text-white">
            {title}
          </h3>
        </div>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
          {statusContent}
        </div>
      </div>
      {isConnected ? (
        identifierValue ? (
          <p className="text-xs text-textPrimary">
            {identifierLabel}:{" "}
            <span className="font-medium text-textTitle dark:text-white break-all">
              {identifierValue}
            </span>
          </p>
        ) : (
          <p className="text-xs text-textPrimary">
            Keine {identifierLabel} verfügbar.
          </p>
        )
      ) : action ? null : (
        <p className="text-xs text-textPrimary">Kein Account verknüpft.</p>
      )}
    </div>
  );
};

const Settings = () => {
  const queryClient = useQueryClient();
  const [vaType, setVaType] = useState("discord");
  const [vaIdentifier, setVaIdentifier] = useState("");
  const [vaFormError, setVaFormError] = useState("");
  const [vaTableError, setVaTableError] = useState("");
  const [removeTarget, setRemoveTarget] = useState("");
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["settings", "subscription"],
    queryFn: getSubscriptionSettingsAPI,
  });
  const {
    data: vaData,
    isLoading: isVaLoading,
    isError: isVaError,
    error: vaErrorObj,
  } = useQuery({
    queryKey: ["settings", "va-access"],
    queryFn: getVaAccessListAPI,
  });

  const isSellerConnected = Boolean(data?.seller_id);
  const isDiscordConnected = Boolean(data?.discord_id);
  const {
    data: sellerUrlData,
    isLoading: isSellerUrlLoading,
    isError: isSellerUrlError,
    error: sellerUrlError,
  } = useQuery({
    queryKey: ["integration", "amazon", "url"],
    queryFn: getAmazonIntegrationUrlAPI,
    enabled: Boolean(data) && !isSellerConnected,
  });
  const sellerOauthUrl = sellerUrlData?.url;

  const discordRedirectUri =
    typeof window !== "undefined"
      ? `${window.location.origin}/integration/discord/oauth`
      : "";
  const {
    data: discordUrlData,
    isLoading: isDiscordUrlLoading,
    isError: isDiscordUrlError,
    error: discordUrlError,
  } = useQuery({
    queryKey: ["integration", "discord", "url", discordRedirectUri],
    queryFn: () => getDiscordIntegrationUrlAPI(discordRedirectUri),
    enabled:
      Boolean(data) && !isDiscordConnected && Boolean(discordRedirectUri),
  });
  const discordOauthUrl = discordUrlData?.url;

  const createIntegrationAction = (
    isLoading,
    isError,
    errorObj,
    connectUrl
  ) => {
    if (isLoading) {
      return (
        <span className="inline-flex items-center gap-2 text-xs text-textPrimary">
          <i className="bx bx-loader-alt animate-spin text-sm"></i>
          Verknüpfungslink wird geladen …
        </span>
      );
    }

    if (isError) {
      return (
        <span className="text-xs text-red-600 dark:text-red-400">
          {errorObj?.message || "Link konnte nicht geladen werden."}
        </span>
      );
    }

    if (connectUrl) {
      return (
        <Button
          href={connectUrl}
          target="_blank"
          rel="noreferrer"
          variant="danger"
          size="sm"
          className="gap-2 self-start shadow-md shadow-red-400/40"
        >
          <i className="bx bx-unlink text-base"></i>
          <span className="flex flex-col text-left leading-tight">
            <span className="text-[10px] uppercase tracking-wide opacity-80">
              Nicht verbunden
            </span>
            <span className="text-sm font-semibold">Jetzt verbinden</span>
          </span>
        </Button>
      );
    }

    return null;
  };

  const sellerActionContent = createIntegrationAction(
    isSellerUrlLoading,
    isSellerUrlError,
    sellerUrlError,
    sellerOauthUrl
  );

  const discordActionContent = createIntegrationAction(
    isDiscordUrlLoading,
    isDiscordUrlError,
    discordUrlError,
    discordOauthUrl
  );

  const activeVas = vaData?.active_vas || [];
  const vaLimit = vaData?.va_limit ?? 0;
  const isLimitReached = vaLimit > 0 && activeVas.length >= vaLimit;
  const isValidDiscordId = (value) => /^\d{12,25}$/.test(value);
  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const trimmedVaIdentifier = vaIdentifier.trim();
  const vaValidationMessage = (() => {
    if (!trimmedVaIdentifier) {
      return "";
    }
    if (vaType === "discord" && !isValidDiscordId(trimmedVaIdentifier)) {
      return "Discord-ID muss 12 bis 25 Ziffern enthalten.";
    }
    if (vaType === "email" && !isValidEmail(trimmedVaIdentifier)) {
      return "Bitte eine gültige E-Mail-Adresse eingeben.";
    }
    return "";
  })();

  const addVaMutation = useMutation({
    mutationFn: (payload) => addVaAccessAPI(payload),
    onSuccess: (response) => {
      if (response?.success === false) {
        setVaFormError(
          response?.message || "VA konnte nicht hinzugefügt werden."
        );
        queryClient.invalidateQueries(["settings", "va-access"]);
        return;
      }
      toast.success("VA wurde hinzugefügt.");
      setVaIdentifier("");
      setVaFormError("");
      queryClient.invalidateQueries(["settings", "va-access"]);
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Fehler beim Hinzufügen des VA.";
      setVaFormError(message);
    },
  });

  const removeVaMutation = useMutation({
    mutationFn: (payload) => removeVaAccessAPI(payload),
    onMutate: (payload) => {
      setRemoveTarget(`${payload.vaType}:${payload.vaIdentifier}`);
      setVaTableError("");
    },
    onSuccess: (response) => {
      if (response?.success === false) {
        setVaTableError(
          response?.message || "VA konnte nicht entfernt werden."
        );
        queryClient.invalidateQueries(["settings", "va-access"]);
        return;
      }
      toast.success("VA wurde entfernt.");
      setVaTableError("");
      queryClient.invalidateQueries(["settings", "va-access"]);
    },
    onError: (err) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Fehler beim Entfernen des VA.";
      setVaTableError(message);
    },
    onSettled: () => {
      setRemoveTarget("");
    },
  });

  const handleAddVa = (event) => {
    event.preventDefault();
    setVaFormError("");

    if (isLimitReached) {
      setVaFormError(`Limit von ${vaLimit} VAs erreicht.`);
      return;
    }

    if (!trimmedVaIdentifier) {
      setVaFormError(
        vaType === "email"
          ? "Bitte gib eine E-Mail-Adresse ein."
          : "Bitte gib eine Discord-ID ein."
      );
      return;
    }

    if (vaValidationMessage) {
      setVaFormError(vaValidationMessage);
      return;
    }

    addVaMutation.mutate({
      vaType,
      vaIdentifier: trimmedVaIdentifier,
    });
  };

  const handleRemoveVa = (payload) => {
    if (!payload?.vaIdentifier) {
      return;
    }
    removeVaMutation.mutate(payload);
  };

  const addDisabled =
    !trimmedVaIdentifier ||
    Boolean(vaValidationMessage) ||
    isLimitReached ||
    addVaMutation.isLoading;
  const cancelUrl = "/churn";

  return (
    <Layout>
      <section className="overflow-auto w-full h-full flex flex-col bg-bgColor">
        <div className="w-full max-w-5xl mx-auto px-6 py-8 space-y-6 text-textTitle dark:text-white">
          <header className="space-y-2">
            <h1 className="text-2xl font-semibold">Einstellungen</h1>
            <p className="text-sm text-textPrimary">
              Verwalte dein Abonnement.
            </p>
          </header>

          {isLoading && (
            <div className="flex items-center gap-3 rounded-lg border border-borderBottom bg-bgColor px-4 py-6 text-textPrimary">
              <i className="bx bx-loader-alt animate-spin text-xl text-primary"></i>
              <span>Abonnementdaten werden geladen …</span>
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200">
              <p className="font-semibold">Fehler beim Laden.</p>
              <p>{error?.message || "Bitte versuche es später erneut."}</p>
            </div>
          )}

          <div className="space-y-6">
            {!isLoading && !isError && data && (
              <>
                <section className="rounded-lg border border-borderBottom bg-bgColor px-6 py-6 shadow-sm">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-4">
                      {data.avatar && (
                        <img
                          src={data.avatar}
                          alt="Nutzer Avatar"
                          className="h-16 w-16 rounded-full object-cover border border-borderBottom"
                        />
                      )}
                      <div className="space-y-1">
                        <h2 className="text-lg font-semibold">
                          {data.customer_name || "Unbekannter Kunde"}
                        </h2>
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-3">
                      <Button
                        href={data.upgrade_url || "#"}
                        target={data.upgrade_url ? "_blank" : undefined}
                        rel={data.upgrade_url ? "noreferrer" : undefined}
                        variant="primary"
                        size="md"
                        aria-disabled={!data.upgrade_url}
                        className={`shadow-md shadow-emerald-300/40 ${
                          !data.upgrade_url ? "pointer-events-none opacity-60" : ""
                        }`}
                      >
                        Upgrade sichern
                      </Button>
                      <Button
                        href={data.manage_url || "#"}
                        target={data.manage_url ? "_blank" : undefined}
                        rel={data.manage_url ? "noreferrer" : undefined}
                        variant="primary-light"
                        size="sm"
                        aria-disabled={!data.manage_url}
                        className={`text-white ${
                          !data.manage_url ? "pointer-events-none opacity-60" : ""
                        }`}
                      >
                        Mitgliedschaft verwalten
                      </Button>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    <InfoItem label="Mitgliedschaft" value={data.membership} />
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-2 rounded-lg border border-borderBottom bg-background px-4 py-3 text-sm text-textPrimary">
                    <i className="bx bx-receipt text-base text-primary"></i>
                    <div className="flex flex-1 flex-wrap items-center gap-1">
                      {data.manage_url ? (
                        <p className="flex flex-wrap items-center gap-1">
                          Alle Rechnungen findest du unter{" "}
                          <a
                            href={data.manage_url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-semibold text-primary hover:underline"
                          >
                            Mitgliedschaft verwalten
                          </a>
                          .
                        </p>
                      ) : (
                        <p>Alle Rechnungen findest du unter Mitgliedschaft verwalten.</p>
                      )}
                    </div>
                    <a
                      href={cancelUrl}
                      className="ml-auto text-xs font-semibold text-textPrimary underline decoration-dotted underline-offset-4 hover:text-textTitle"
                    >
                      Edge Kündigen
                    </a>
                  </div>
                </section>

                <section className="rounded-lg border border-borderBottom bg-bgColor px-6 py-6 shadow-sm">
                  <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">Integrationen</h2>
                      <p className="text-sm text-textPrimary">
                        Verbinde Drittanbieter-Konten.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <IntegrationCard
                      title="Discord"
                      isConnected={isDiscordConnected}
                      identifierLabel="Discord ID"
                      identifierValue={data.discord_id}
                      action={discordActionContent}
                    />
                    <IntegrationCard
                      title="Seller Central"
                      isConnected={isSellerConnected}
                      identifierLabel="Seller ID"
                      identifierValue={data.seller_id}
                      action={sellerActionContent}
                    />
                  </div>
                </section>
              </>
            )}

            <section className="rounded-lg border border-borderBottom bg-bgColor px-6 py-6 shadow-sm">
              <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold">VA Management</h2>
                  <p className="text-sm text-textPrimary">
                    Vergib und verwalte Zugänge für virtuelle Assistenten.
                  </p>
                </div>
                {!isVaLoading && !isVaError && (
                  <span className="text-sm text-textPrimary">
                    Aktiv: {activeVas.length} / {vaLimit || "—"}
                  </span>
                )}
              </div>

              <form
                className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4"
                onSubmit={handleAddVa}
              >
                <label className="min-w-[140px]">
                  <span className="text-xs font-semibold uppercase tracking-wide text-textPrimary">
                    Typ
                  </span>
                  <select
                    value={vaType}
                    onChange={(event) => {
                      setVaType(event.target.value);
                      setVaFormError("");
                    }}
                    className="mt-1 w-full rounded border border-borderBottom bg-background px-3 py-2 text-sm text-textTitle focus:border-primary focus:outline-none"
                  >
                    <option value="discord">Discord</option>
                    <option value="email">E-Mail</option>
                  </select>
                </label>
                <label className="flex-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-textPrimary">
                    {vaType === "email" ? "E-Mail" : "Discord-ID"}
                  </span>
                  <input
                    type="text"
                    value={vaIdentifier}
                    onChange={(event) => {
                      setVaIdentifier(event.target.value);
                      setVaFormError("");
                    }}
                    placeholder={
                      vaType === "email"
                        ? "va@example.com"
                        : "z. B. 654780874176331786"
                    }
                    className="mt-1 w-full rounded border border-borderBottom bg-background px-3 py-2 text-sm text-textTitle placeholder:text-textPrimary dark:placeholder:text-textsecondary focus:border-primary focus:outline-none"
                  />
                </label>
                <button
                  type="submit"
                  disabled={addDisabled}
                  className="inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-main to-primary px-4 py-2 text-sm font-semibold text-white dark:text-black shadow-md shadow-main/40 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {addVaMutation.isLoading ? (
                    <>
                      <i className="bx bx-loader-alt animate-spin text-base"></i>
                      Hinzufügen …
                    </>
                  ) : (
                    <>
                      <i className="bx bx-plus text-base"></i>
                      VA hinzufügen
                    </>
                  )}
                </button>
              </form>

              {vaFormError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {vaFormError}
                </p>
              )}
              {!vaFormError && vaValidationMessage && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {vaValidationMessage}
                </p>
              )}
              {!vaFormError && isLimitReached && (
                <p className="mt-2 text-sm text-textPrimary">
                  Du hast das Limit von {vaLimit} VAs erreicht. Entferne zuerst
                  einen Zugang, bevor du einen neuen einlädst.
                </p>
              )}

              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-borderBottom text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-textPrimary">
                    <tr>
                      <th className="px-3 py-2 font-medium">Typ</th>
                      <th className="px-3 py-2 font-medium">Kennung</th>
                      <th className="px-3 py-2 font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderBottom text-sm text-textPrimary">
                    {isVaLoading && (
                      <tr>
                        <td
                          className="px-3 py-4 text-center text-textPrimary"
                          colSpan={3}
                        >
                          <span className="inline-flex items-center gap-2">
                            <i className="bx bx-loader-alt animate-spin text-base text-primary"></i>
                            Lade VA-Daten …
                          </span>
                        </td>
                      </tr>
                    )}

                    {isVaError && (
                      <tr>
                        <td
                          className="px-3 py-4 text-center text-red-600 dark:text-red-400"
                          colSpan={3}
                        >
                          {vaErrorObj?.message ||
                            "Fehler beim Laden der VA-Daten."}
                        </td>
                      </tr>
                    )}

                    {!isVaLoading && !isVaError && activeVas.length === 0 && (
                      <tr>
                        <td
                          className="px-3 py-4 text-center text-textPrimary"
                          colSpan={3}
                        >
                          Keine VAs hinterlegt.
                        </td>
                      </tr>
                    )}

                    {!isVaLoading &&
                      !isVaError &&
                      activeVas.map((va, index) => {
                        const vaTypeValue =
                          va.va_type || (va.va_discord_id ? "discord" : "email");
                        const identifier =
                          va.va_identifier || va.va_discord_id || "";
                        const key = `${vaTypeValue}-${identifier || index}`;
                        const isRemovingThis =
                          removeVaMutation.isLoading &&
                          removeTarget === `${vaTypeValue}:${identifier}`;

                        return (
                          <tr key={key}>
                            <td className="px-3 py-3 font-medium text-textTitle dark:text-white">
                              {vaTypeValue === "email" ? "E-Mail" : "Discord"}
                            </td>
                            <td className="px-3 py-3 font-medium text-textTitle dark:text-white">
                              {identifier || "—"}
                            </td>
                            <td className="px-3 py-3">
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveVa({
                                    vaType: vaTypeValue,
                                    vaIdentifier: identifier,
                                  })
                                }
                                disabled={
                                  removeVaMutation.isLoading || !identifier
                                }
                                className="inline-flex items-center gap-2 rounded border border-borderBottom px-3 py-1 text-sm text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                                title="VA entfernen"
                              >
                                {isRemovingThis ? (
                                  <i className="bx bx-loader-alt animate-spin text-base"></i>
                                ) : (
                                  <i className="bx bx-trash text-base"></i>
                                )}
                                Entfernen
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {vaTableError && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {vaTableError}
                </p>
              )}
            </section>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Settings;
